import React, { useEffect, useRef, useState } from "react";
import { ArrowUpDown, Check } from "lucide-react";

/**
 * SortMenu — icon-only Sort dropdown for the Orders toolbar. Ships each
 * user-visible option as `<field>:<asc|desc>` so the parent can just pass it
 * straight to `GET /api/orders?sort_by=…&sort_dir=…`. Feb 28 2026.
 */
const SORT_OPTIONS = [
  { key: "created_at:desc",               label: "Created Date (New to Old)" },
  { key: "created_at:asc",                label: "Created Date (Old to New)" },
  { key: "item_price:desc",               label: "Purchase Amount (High to Low)" },
  { key: "item_price:asc",                label: "Purchase Amount (Low to High)" },
  { key: "purchase_date:desc",            label: "Purchase Date (New to Old)" },
  { key: "purchase_date:asc",             label: "Purchase Date (Old to New)" },
  // Sep 7 2026 — remaining date columns exposed for chronological sort.
  // Every field is stored as an ISO date string (or ISO datetime) so
  // Mongo's lexical sort == chronological sort. The frontend renders the
  // same values via formatDMY ("07-Sep-2026") but sorts on the raw ISO.
  { key: "shipping_date:desc",            label: "Shipping Date (New to Old)" },
  { key: "shipping_date:asc",             label: "Shipping Date (Old to New)" },
  { key: "claim_raised_date:desc",        label: "Claim Raised (New to Old)" },
  { key: "claim_raised_date:asc",         label: "Claim Raised (Old to New)" },
  { key: "china_order_date:desc",         label: "China Order Date (New to Old)" },
  { key: "china_order_date:asc",          label: "China Order Date (Old to New)" },
  { key: "china_order_dispatched_date:desc", label: "China Dispatched (New to Old)" },
  { key: "china_order_dispatched_date:asc",  label: "China Dispatched (Old to New)" },
  { key: "china_order_received_date:desc",   label: "China Received (New to Old)" },
  { key: "china_order_received_date:asc",    label: "China Received (Old to New)" },
  { key: "delivered_date:desc",           label: "Delivered Date (New to Old)" },
  { key: "delivered_date:asc",            label: "Delivered Date (Old to New)" },
  { key: "qty_purchased:desc",            label: "Qty Purchased (High to Low)" },
  { key: "qty_purchased:asc",             label: "Qty Purchased (Low to High)" },
  { key: "inventory_allocated_qty:desc",  label: "Allocated (Yes first)" },
  { key: "inventory_allocated_qty:asc",   label: "Allocated (No first)" },
];

export default function SortMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (key) => {
    // Toggle-off if the user re-clicks the currently active sort — falls back
    // to the backend's default system_id-desc ordering.
    onChange(key === value ? "" : key);
    setOpen(false);
  };

  return (
    <div className="relative" ref={rootRef} data-testid="orders-sort-menu">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="orders-sort-btn"
        title="Sort"
        className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      >
        <ArrowUpDown size={14} />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 z-40 bg-white border border-slate-200 rounded-sm shadow-lg w-[260px] overflow-hidden"
          data-testid="orders-sort-menu-panel"
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100">
            Sort by
          </div>
          {SORT_OPTIONS.map((o) => {
            const active = o.key === value;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => pick(o.key)}
                data-testid={`orders-sort-opt-${o.key}`}
                className={`w-full text-left px-3 py-1.5 text-[12.5px] inline-flex items-center gap-2 border-b border-slate-50 last:border-0 ${
                  active ? "bg-blue-50/70 text-blue-900 font-semibold" : "hover:bg-slate-50 text-slate-700"
                }`}
              >
                <span className="w-4 shrink-0">
                  {active && <Check size={13} className="text-blue-700" />}
                </span>
                <span className="flex-1">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
